import { AddSimple } from '@tcl-boron-icons/icons';
import { useState } from 'react';
import AttachmentViewer from './AttachmentViewer';

import Dropzone from 'react-dropzone';
import { Grid } from '@mui/material';
import LoadingIndicator from './LoadingIndicator';
import { createUseStyles } from 'react-jss';

export interface CachedFile extends File {
  content?: string;
}

type UploadZoneProps = {
  docPreview?: string;
  docType?: string;
  setFile: (file?: File) => Promise<void>;
  width?: string;
};

const MAX_FILE_SIZE_BYTES = 5000000;

const useStyles = createUseStyles({
  dropzone: {
    cursor: 'pointer',
  },
  hiddenInput: {
    display: 'none',
  },
});

const UploadZone: React.FC<UploadZoneProps> = ({ docPreview, docType, setFile, width = '128px' }) => {
  const [fileLoading, setFileLoading] = useState(false);
  const classes = useStyles();

  const uploadFile = async (file: File) => {
    if (file && file.size > MAX_FILE_SIZE_BYTES) {
      alert('File is too large. Maximum allowed file size is 5MB. Please try again.');
      return;
    }

    setFileLoading(true);
    await setFile(file);
    setFileLoading(false);
  };

  return fileLoading ? (
    <LoadingIndicator size="small" />
  ) : docPreview && docType ? (
    <Grid item container direction="column" gap="16px" justifyContent="center" alignItems="center" width={width}>
      <AttachmentViewer doc={docPreview} docType={docType} />
    </Grid>
  ) : (
    <Dropzone
      onDrop={(file) => uploadFile(file[0])}
      maxFiles={1}
      onDropRejected={(errors) => {
        const err = errors[0];
        if (err.errors[0].code === 'file-too-large') {
          alert('File is too large. Maximum allowed file size is 5MB. Please try again.');
        } else {
          alert(
            'Something went wrong. Please try again and ensure that the file you are uploading is of a valid format (PNG, JPG, JPEG, or PDF) and is no larger than 5MB.',
          );
        }
      }}
      maxSize={MAX_FILE_SIZE_BYTES}
      accept={{
        'image/*': ['.png', '.jpg', '.jpeg'],
        'application/pdf': ['.pdf'],
      }}
    >
      {({ getRootProps, getInputProps }) => (
        <Grid
          item
          container
          width={width}
          padding="30px"
          justifyContent="center"
          alignItems="center"
          border="1px dashed black"
          data-testid="upload-zone"
          className={classes.dropzone}
          {...getRootProps()}
        >
          <input {...getInputProps()} className={classes.hiddenInput} data-testid="upload-zone-input" />
          <AddSimple data-testid="upload-zone-icon" />
        </Grid>
      )}
    </Dropzone>
  );
};

export default UploadZone;
